import type { CodigoErroNotaFiscal, MotivoRecusaArquivoNf } from '@/types/comissao'
import { TEXTO_MOTIVO_RECUSA, ehMotivoConhecido } from './verificacaoArquivoNf'

/**
 * AN-9 (L10 · Q36): os 409/422 do fluxo da nota fiscal viram texto que diz ao usuário o que
 * fazer. O código vem no `errorCode` do corpo (envelope `RequisicaoFalhou.data` do `api.ts`);
 * a mensagem do backend, quando existe, vai junto — ela traz os valores (esperado × encontrado).
 */
const TEXTO: Record<CodigoErroNotaFiscal, string> = {
  NF_VALOR_FORA_TOLERANCIA:
    'O valor da nota não bate com a soma das comissões marcadas, nem dentro da tolerância definida pelo Financeiro. Confira o valor digitado e as comissões marcadas.',
  NF_DUPLICADA:
    'Você já registrou uma nota com este número e outros dados. Confira o número; se a nota anterior estiver errada, retire-a antes de reemitir.',
  IDEMPOTENCIA_CONFLITO:
    'Este envio já tinha sido registrado com outros dados. Confira os dados e registre de novo.',
  NF_ESTADO_NAO_PERMITE_ANEXO:
    'A nota não aceita mais arquivo nesta etapa (já aprovada, paga ou recusada).',
  NF_RETENCOES_INCOERENTES:
    'Valor dos serviços menos as retenções não fecha com o valor líquido informado. Confira as retenções e o líquido.',
  NF_JA_PAGA: 'Esta nota já foi paga com outros dados de pagamento.',
  NF_ESTADO_INVALIDO:
    'A nota mudou de etapa enquanto você trabalhava. Recarregue a lista e tente de novo.',
  NF_COM_PENDENCIA: 'A nota tem pendência aberta: resolva antes de aprovar ou pagar.',
  NF_NAO_APROVADA: 'A nota desta comissão ainda não tem aprovação fiscal.',
  NF_SEM_ARQUIVO: 'A nota ainda não tem arquivo anexado para conferir.',
  NF_LEITURA_PENDENTE: 'A leitura do arquivo ainda não terminou. Aguarde e tente de novo.',
  NF_CONFERENCIA_DESATUALIZADA:
    'O arquivo da nota mudou depois da conferência. Confira a nota de novo antes de pagar.',
  NF_RETIRADA_NAO_PERMITIDA:
    'Esta nota não pode mais ser retirada (já aprovada, paga ou sem pendência aberta).',
  COMISSAO_SO_BACKOFFICE: 'Esta ação é exclusiva do Financeiro.',
  // Onda 5 (AN-11 §3.3)
  NF_ARQUIVO_NAO_VERIFICADO:
    'O arquivo da nota ainda está em verificação. Aguarde alguns instantes e tente de novo.',
  NF_ARQUIVO_RECUSADO:
    'O arquivo anexado a esta nota foi recusado. O arquivo precisa ser anexado novamente antes de conferir ou aprovar.',
  NF_DOCUMENTO_INVALIDO:
    'Este arquivo não pode ser anexado a esta nota. Envie o arquivo de novo pelo botão "Anexar arquivo" da nota.',
  NF_DOCUMENTO_NO_REGISTRO:
    'O arquivo é anexado depois de registrar a nota, pelo botão "Anexar arquivo".',
  // P5 (AN-PP5 §6.3, §6.4, §7)
  RETENCAO_DIVERGE_SUGESTAO_SEM_JUSTIFICATIVA:
    'Há retenção diferente da sugerida pela política: informe a justificativa da divergência.',
  POLITICA_RETENCAO_INCOMPLETA:
    'A política de retenção não cobre todos os tributos desta nota, e este ambiente exige sugestão completa para aprovar.',
  REGIME_TRIBUTARIO_OBRIGATORIO: 'Informe o regime tributário do prestador para sugerir as retenções.',
  MUNICIPIO_INVALIDO: 'Município de incidência do ISS: informe o código IBGE de 7 dígitos.',
  TOLERANCIA_POR_TENANT_PROIBIDA:
    'A tolerância é única para todos os tenants (Q76): o override do tenant só define o CNPJ do tomador.',
  TOLERANCIA_OBRIGATORIA: 'A política global exige a tolerância (use 0 para exigir valor exato).',
  CNPJ_TOMADOR_OBRIGATORIO: 'O override do tenant existe só para o CNPJ do tomador: informe-o.',
}

export const codigoErro = (e: unknown): string | undefined => {
  const data = (e as { data?: { errorCode?: unknown } } | null)?.data
  return typeof data?.errorCode === 'string' ? data.errorCode : undefined
}

/**
 * Corpo extra do erro (`ApiResponse.data` do backend, em `RequisicaoFalhou.data.data`): sugestão de
 * competência, meses com projetadas, divergências de retenção… `undefined` quando não vem objeto.
 */
export const dadosDoErro = <T extends object>(e: unknown): Partial<T> | undefined => {
  const d = (e as { data?: { data?: unknown } } | null)?.data?.data
  return d && typeof d === 'object' ? (d as Partial<T>) : undefined
}

export const statusDoErro = (e: unknown): number | undefined => {
  const s = (e as { status?: unknown } | null)?.status
  return typeof s === 'number' ? s : undefined
}

const mensagemBackend = (e: unknown): string | undefined => {
  const m = (e as { message?: unknown } | null)?.message
  return typeof m === 'string' && m.trim() ? m : undefined
}

/** Códigos cujo `data.motivo` é um {@link MotivoRecusaArquivoNf}. */
const CODIGOS_COM_MOTIVO_DO_ARQUIVO = new Set(['NF_DOCUMENTO_INVALIDO', 'NF_ARQUIVO_RECUSADO'])

/**
 * `NF_DOCUMENTO_INVALIDO` (422) e `NF_ARQUIVO_RECUSADO` (409) trazem o código do motivo em
 * `data.motivo` (envelope `ApiResponse` do backend: `RequisicaoFalhou.data.data.motivo`).
 * `undefined` em qualquer outro erro ou com um código desconhecido.
 */
export const motivoDocumentoInvalido = (e: unknown): MotivoRecusaArquivoNf | undefined => {
  const codigo = codigoErro(e)
  if (!codigo || !CODIGOS_COM_MOTIVO_DO_ARQUIVO.has(codigo)) return undefined
  const motivo = (e as { data?: { data?: { motivo?: unknown } | null } } | null)?.data?.data?.motivo
  return ehMotivoConhecido(motivo) ? motivo : undefined
}

/**
 * Texto legível do erro; `padrao` quando não há código conhecido nem mensagem.
 *
 * <p>Nos erros do arquivo da nota a mensagem do backend NÃO é exibida: para o back-office ela traz
 * o detalhe da recusa (de que empresa/entidade é o arquivo). A tela usa o texto local do código
 * de `data.motivo`, sempre neutro.
 */
export const mensagemErroNota = (e: unknown, padrao: string): string => {
  const codigo = codigoErro(e)
  const backend = mensagemBackend(e)
  if (codigo && codigo in TEXTO) {
    const texto = TEXTO[codigo as CodigoErroNotaFiscal]
    if (CODIGOS_COM_MOTIVO_DO_ARQUIVO.has(codigo)) {
      const motivo = motivoDocumentoInvalido(e)
      return motivo ? `${texto} (Motivo: ${TEXTO_MOTIVO_RECUSA[motivo]}.)` : texto
    }
    if (backend && backend !== texto) return `${texto} (${backend})`
    return texto
  }
  return backend ?? padrao
}
